import { useContext, useEffect, useMemo, useState } from "react";
import useMappers from "./useMappers";
import { GeaoContext } from "../../../GeaoContextProvider";
import { PageStatus } from "../../../hooks/useInterviewUpdater";
import { useNavigate } from "react-router-dom";
import { initialize, updateMany } from "../../../../../api/onboarding";
import { GeaoPaths } from "../../../constants/GeaoPaths";
import { ApiRequestStatus as Status } from "../../../constants/ApiRequestStatus";

export default function useFormEvents(
    trigger = () => { return Promise.resolve(); },
    getValues = () => { }
) {
    const navigate = useNavigate();

    const {
        agencyInterview,
        setAgencyInterview,
        agentInterview,
        setAgentInterview,
        branchInterview1,
        setBranchInterview1,
        branchInterview2,
        setBranchInterview2
    } = useContext(GeaoContext);

    const [branch1InitializeStatus, setBranch1InitializeStatus] = useState(Status.NotSent);
    const [branch2InitializeStatus, setBranch2InitializeStatus] = useState(Status.NotSent);
    const [updateManyStatus, setUpdateManyStatus] = useState(Status.NotSent);

    const [formData, setFormData] = useState();
    const [isOnSaveAndExit, setIsOnSaveAndExit] = useState(false);

    const saving = useMemo(() => (
        updateManyStatus === Status.Pending ||
        branch1InitializeStatus === Status.Pending ||
        branch2InitializeStatus === Status.Pending
    ), [
        updateManyStatus,
        branch1InitializeStatus,
        branch2InitializeStatus
    ]);

    const {
        agencyInterviewQuestionAnswers,
        branch1InterviewQuestionAnswers,
        branch2InterviewQuestionAnswers
    } = useMappers(formData);

    useEffect(() => {
        if (formData) {
            upsertInterviews(formData);
        }
    }, [formData]);

    useEffect(() => {
        const nextPath = isOnSaveAndExit ?
            GeaoPaths.CreateYourProfileChecklist :
            GeaoPaths.PrincipalAgencyEmployeeInformation;

        if (
            updateManyStatus === Status.Success &&
            (branch1InitializeStatus === Status.NotSent || branch1InitializeStatus === Status.Success) &&
            (branch2InitializeStatus === Status.NotSent || branch2InitializeStatus === Status.Success)
        ) {
            navigate(nextPath);
        }
    }, [
        branch1InitializeStatus,
        branch2InitializeStatus,
        updateManyStatus,
        saving
    ]);

    const onSubmit = (formData) => {
        if (!saving) {
            setIsOnSaveAndExit(false);
            setFormData(formData);
        }
    };

    const onSaveAndExit = () => {
        if (!saving) {
            trigger().then(validationResult => {
                if (validationResult) {
                    setIsOnSaveAndExit(true);
                    setFormData(getValues());
                }
            });
        }
    };

    const upsertInterviews = (formData) => {
        setUpdateManyStatus(Status.NotSent);
        setBranch1InitializeStatus(Status.NotSent);
        setBranch2InitializeStatus(Status.NotSent);

        const activityRequest = isOnSaveAndExit ?
            {
                ActivityCategory: "GEAO_ONBOARDING",
                ActivitySubType: "SAVE_AND_EXIT",
                SourceApplication: "AGIWUI",
                AdditionalData: {
                    ComponentName: "PrincipalAgencyOfficeInformation"
                }
            } : null;

        const updatedAgentInterview = {
            ...agentInterview,
            pageStatus: {
                ...agentInterview.pageStatus,
                "PrincipalAgencyOfficeInformation": PageStatus.Completed
            }
        };

        const updateAgentInterviewRequest = {
            identityType: "Agent",
            interview: updatedAgentInterview,
            activityRequest: activityRequest
        };

        const updatedAgencyInterview = {
            ...agencyInterview,
            questionAnswers: {
                ...agencyInterview.questionAnswers,
                ...agencyInterviewQuestionAnswers
            },
            pageStatus: {
                ...agencyInterview.pageStatus,
                "PrincipalAgencyOfficeInformation": PageStatus.Completed
            }
        };

        const updateAgencyInterviewRequest = {
            identityType: "Agency",
            interview: updatedAgencyInterview
        }

        const hasOneOrTwoAdditionalCoo = formData.centersOfOperationOutsidePrimaryLocation &&
            (
                formData.numberOfCentersOfOperationUnderSameEin === "one" ||
                formData.numberOfCentersOfOperationUnderSameEin === "two"
            );

        const updatedBranchInterview1 = hasOneOrTwoAdditionalCoo ? {
            ...(branchInterview1 ?? {}),
            questionAnswers: {
                ...(branchInterview1?.questionAnswers ?? {}),
                ...branch1InterviewQuestionAnswers
            },
            relationships: [
                ...(branchInterview1?.relationships ?? []),
                { type: "ParentAgency", interviewId: agencyInterview.interviewId }
            ]
        } : null;

        const updateBranch1InterviewRequest = {
            identityType: "Agency",
            interview: updatedBranchInterview1
        }

        const hasTwoAdditionalCentersOfOperation = formData.centersOfOperationOutsidePrimaryLocation &&
            formData.numberOfCentersOfOperationUnderSameEin === "two";

        const updatedBranchInterview2 = hasTwoAdditionalCentersOfOperation ? {
            ...(branchInterview2 ?? {}),
            questionAnswers: {
                ...(branchInterview2?.questionAnswers ?? {}),
                ...branch2InterviewQuestionAnswers
            },
            relationships: [
                ...(branchInterview2?.relationships ?? []),
                { type: "ParentAgency", interviewId: agencyInterview.interviewId }
            ]
        } : null;

        const updateBranch2InterviewRequest = {
            identityType: "Agency",
            interview: updatedBranchInterview2
        };

        setUpdateManyStatus(Status.Pending);

        updateMany([
            updateAgencyInterviewRequest,
            updateAgentInterviewRequest,
            ...(branchInterview1 && hasOneOrTwoAdditionalCoo ? [updateBranch1InterviewRequest] : []),
            ...(branchInterview2 && hasTwoAdditionalCentersOfOperation ? [updateBranch2InterviewRequest] : [])
        ], responseInterviews => {
            // Success callback
            setAgencyInterview(responseInterviews.find(i => i.interviewId === agencyInterview.interviewId));
            setAgentInterview(responseInterviews.find(i => i.interviewId === agentInterview.interviewId));

            const responseBranch1 = branchInterview1 ? responseInterviews.find(i => i.interviewId === branchInterview1.interviewId) : null;

            if (responseBranch1) {
                setBranchInterview1(responseBranch1);
            }

            const responseBranch2 = branchInterview2 ? responseInterviews.find(i => i.interviewId === branchInterview2.interviewId) : null;

            if (responseBranch2) {
                setBranchInterview2(responseBranch2);
            }

            setUpdateManyStatus(Status.Success);
        },
            () => {
                // Finally callback
            },
            () => {
                // Error callback
                setUpdateManyStatus(Status.Error);
            });

        if (!branchInterview1 && hasOneOrTwoAdditionalCoo) {
            setBranch1InitializeStatus(Status.Pending);

            initialize(updateBranch1InterviewRequest)
                .then(responseInterview => {
                    setBranchInterview1(responseInterview);
                    setBranch1InitializeStatus(Status.Success);
                })
                .catch(_ => { setBranch1InitializeStatus(Status.Error); });;
        }

        if (!branchInterview2 && hasTwoAdditionalCentersOfOperation) {
            setBranch2InitializeStatus(Status.Pending);

            initialize(updateBranch2InterviewRequest)
                .then(responseInterview => {
                    setBranchInterview2(responseInterview);
                    setBranch2InitializeStatus(Status.Success);
                })
                .catch(_ => { setBranch2InitializeStatus(Status.Error); });
        }
    }

    return {
        onSubmit,
        onSaveAndExit,
        saving
    };
}
